import { brand } from "../brand.js";

export type PlaybackQuotaKind = "live" | "series" | "movie";
export type EntitlementMode = "free" | "paid";

export const PLAYBACK_LIMIT_SECONDS: Record<PlaybackQuotaKind, number> = {
  // Live TV is unlimited on the free tier (owner, 2026-10-13; ADR 2026-10-10). Matches
  // PlaybackLimitsPlugin.limitMs on the native side.
  live: Number.POSITIVE_INFINITY,
  series: 30 * 60,
  // 145 minutes (owner, 2026-10-13): enough to watch a full movie. Matches
  // PlaybackLimitsPlugin.limitMs on the native side, which does the real counting in the APK.
  movie: 145 * 60,
};

const STORAGE_KEY = "slique-playback-usage-v2";
const INSTALLATION_KEY = "slique-installation-id";

type Usage = Record<PlaybackQuotaKind, number>;
interface StoredUsage {
  installationId: string;
  day: string;
  entitlement: EntitlementMode;
  used: Usage;
}

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
}

const PLUGIN = "PlaybackLimits";
const emptyUsage = (): Usage => ({ live: 0, series: 0, movie: 0 });
/**
 * The device's LOCAL calendar day, "YYYY-MM-DD": the allowance resets at local
 * midnight, as the native side (PlaybackLimitsPlugin) does. toISOString() is a
 * UTC date, which rolled the day over at 7 PM in UTC-5 and made the two sides
 * disagree until midnight.
 */
export function localDay(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const today = (): string => localDay();

function bridge(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  return cap?.isNativePlatform?.() === true && typeof cap.nativePromise === "function"
    && cap.PluginHeaders?.some((header) => header.name === PLUGIN) ? cap : null;
}

function nativeCall(method: string, options: Record<string, unknown> = {}): Promise<unknown> | null {
  const cap = bridge();
  return cap ? cap.nativePromise!(PLUGIN, method, options) : null;
}

function installationId(): string {
  if (typeof localStorage === "undefined") return "ephemeral";
  const existing = localStorage.getItem(INSTALLATION_KEY);
  if (existing) return existing;
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(INSTALLATION_KEY, id);
  return id;
}

function read(): StoredUsage {
  const fallback: StoredUsage = { installationId: installationId(), day: today(), entitlement: "free", used: emptyUsage() };
  if (typeof localStorage === "undefined") return fallback;
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as Partial<StoredUsage> | null;
    if (!parsed || typeof parsed !== "object") return fallback;
    const value: StoredUsage = {
      installationId: typeof parsed.installationId === "string" ? parsed.installationId : fallback.installationId,
      day: typeof parsed.day === "string" ? parsed.day : today(),
      entitlement: parsed.entitlement === "paid" ? "paid" : "free",
      used: {
        live: Math.max(0, Number(parsed.used?.live) || 0),
        series: Math.max(0, Number(parsed.used?.series) || 0),
        movie: Math.max(0, Number(parsed.used?.movie) || 0),
      },
    };
    if (value.day !== today()) {
      value.day = today();
      value.used = emptyUsage();
      write(value);
    }
    return value;
  } catch {
    return fallback;
  }
}

function write(value: StoredUsage): void {
  if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

/**
 * Whether this build offers the Free/Paid switch: test builds only (native
 * BuildFlags.testTools; owner, 2026-10-20). Known once hydratePlaybackUsage has
 * heard from the app; false before that, on the website, and in a release build.
 */
let switchable = false;

export function entitlementSwitchable(): boolean {
  return switchable;
}

export async function hydratePlaybackUsage(): Promise<void> {
  const result = nativeCall("getState");
  if (!result) return;
  try {
    const state = (await result) as Partial<StoredUsage> & { used?: Partial<Usage>; switchable?: boolean };
    switchable = state.switchable === true;
    const current = read();
    const before = current.entitlement;
    if (state.entitlement === "free" || state.entitlement === "paid") current.entitlement = state.entitlement;
    if (current.entitlement !== before) queueMicrotask(notifyEntitlement);
    if (state.day === today() && state.used) {
      current.used = {
        live: Math.max(0, Number(state.used.live) || 0),
        series: Math.max(0, Number(state.used.series) || 0),
        movie: Math.max(0, Number(state.used.movie) || 0),
      };
    }
    write(current);
  } catch {
    // The web fallback remains usable if an older APK has no plugin method.
  }
}

export function getEntitlement(): EntitlementMode { return read().entitlement; }

const entitlementListeners = new Set<() => void>();

/** Called when the entitlement changes, so screens that lock features follow at once. */
export function subscribeEntitlement(listener: () => void): () => void {
  entitlementListeners.add(listener);
  return () => void entitlementListeners.delete(listener);
}

function notifyEntitlement(): void {
  for (const listener of [...entitlementListeners]) listener();
}

export function setEntitlement(mode: EntitlementMode): void {
  const value = read();
  write({ ...value, entitlement: mode });
  void nativeCall("setEntitlement", { entitlement: mode });
  notifyEntitlement();
}

export function getPlaybackUsage(): Usage { return { ...read().used }; }

export function remainingPlaybackSeconds(kind: PlaybackQuotaKind): number {
  // A tenant's app has no free tier: its viewers pay their provider, not us.
  if (brand().channel === "tenant") return Number.POSITIVE_INFINITY;
  const value = read();
  if (value.entitlement === "paid") return Number.POSITIVE_INFINITY;
  return Math.max(0, PLAYBACK_LIMIT_SECONDS[kind] - value.used[kind]);
}

export function recordPlaybackSeconds(kind: PlaybackQuotaKind, seconds: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return remainingPlaybackSeconds(kind);
  const value = read();
  if (value.entitlement !== "paid") {
    value.used[kind] = Math.min(PLAYBACK_LIMIT_SECONDS[kind], value.used[kind] + seconds);
    write(value);
  }
  void nativeCall("record", { kind, seconds });
  return remainingPlaybackSeconds(kind);
}

export function resetPlaybackUsage(): void {
  const value = read();
  write({ ...value, day: today(), used: emptyUsage() });
  void nativeCall("reset");
}

export class PlaybackTimeMeter {
  private wasPlaying = false;
  private lastAt = 0;

  constructor(private readonly kind: PlaybackQuotaKind) {}

  sample(status: "playing" | string): boolean {
    const now = Date.now();
    if (this.wasPlaying && this.lastAt > 0) {
      const elapsed = Math.min(10, Math.max(0, (now - this.lastAt) / 1000));
      recordPlaybackSeconds(this.kind, elapsed);
    }
    this.lastAt = now;
    this.wasPlaying = status === "playing";
    return remainingPlaybackSeconds(this.kind) <= 0;
  }

  reset(): void {
    this.wasPlaying = false;
    this.lastAt = 0;
  }
}

export function quotaKindFor(
  sourceType: "live" | "vod" | undefined,
  playingContentType: "vod" | "episode" | "catchup" | null | undefined,
): PlaybackQuotaKind {
  if (sourceType === "live" || playingContentType === "catchup") return "live";
  return playingContentType === "episode" ? "series" : "movie";
}
